import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Search, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  cancelStudioByIdFromOriginalCase,
  cancelStudioByPartnerFromOriginalCase,
  cancelStudioSaleOfferFromOriginalCase,
  changeStudioAddressFromOriginalCase,
  closeStudioFromOriginalCase,
  createStudioSaleOffer,
  fetchStudioAddressChangeCandidates,
  fetchStudioCancellationCandidates,
  fetchStudioCancellationPartnerCandidates,
  fetchStudioStateChangeCandidates,
  resumeStudioFromOriginalCase,
  setStudioServiceStatus,
  suspendStudioFromOriginalCase,
  transferStudioToManagementFromOriginalCase,
  updateStudioSaleOfferFromOriginalCase,
  type StudioCancellationCandidate,
  type StudioCancellationPartnerCandidate,
  type StudioClosureType,
  type StudioStateChangeCandidate,
} from '@/services/studioStateChangeService'

export type StudioStateChangeMode =
  | 'sale_offer'
  | 'update_sale_offer'
  | 'cancel_sale_offer'
  | 'service_status'
  | 'resume_studio'
  | 'close_studio'
  | 'cancel_studio'
  | 'transfer_to_management'
  | 'change_address'
  | 'suspend_studio'

const MODE_LABELS: Record<StudioStateChangeMode, string> = {
  sale_offer: 'Студия выставлена на продажу',
  update_sale_offer: 'Студия сменила оффер',
  cancel_sale_offer: 'Студия закрыла оффер',
  service_status: 'Студия нет продаж',
  resume_studio: 'Студия возобновила работу',
  close_studio: 'Студия закрыта',
  cancel_studio: 'Студия отменена',
  transfer_to_management: 'Студия перешла в УК',
  change_address: 'Смена адреса студии',
  suspend_studio: 'Студия временно приостановлена',
}

const STATUS_LABELS: Record<string, string> = {
  CLOSED: 'закрыта',
  OPEN: 'открыта',
  SUSPENDED: 'приостановлена',
  CANCELLED: 'отменена',
  'PRE-OPEN': 'открывается',
  SWITCH_TO_PURCHASE: 'переход на покупку',
  SERVICE: 'нет продаж',
}

const PHASE_LABELS: Record<string, string> = {
  BUYING: 'зашел на покупку',
  OWNED: 'владеет',
  FOR_SALE: 'на продаже',
  PLANNED: 'планируется к открытию',
  SOLD: 'продана',
  TRANSFERRED: 'передана УК',
  NOT_OPENED: 'не открылась',
}

const CURRENCY_OPTIONS = ['RUB', 'BYN', 'KZT', 'AMD', 'KGS', 'AED', 'PLN', 'RON', 'RSD', 'TRY', 'MDL', 'AUD']
const CLOSURE_TYPE_OPTIONS: Array<{ value: StudioClosureType; label: string }> = [
  { value: 'STANDARD', label: 'Обычное закрытие' },
  { value: 'REBRANDING', label: 'Ребрендинг' },
  { value: 'UNAPPROVED', label: 'Не согласовано' },
]

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function matchesQuery(haystack: Array<string | number | null | undefined>, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return haystack
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function dateAtTen(value: string): string | null {
  return value ? `${value} 10:00:00` : null
}

function dateAtNoon(value: string): string | null {
  return value ? `${value} 12:00:00` : null
}

function dateAtTwo(value: string): string | null {
  return value ? `${value} 14:00:00` : null
}

function dateAtStart(value: string): string | null {
  return value ? `${value} 00:00:00` : null
}

function formatMoney(value: number | null, currency: string | null): string {
  if (value == null) return 'нет'
  return `${value.toLocaleString('ru-RU')} ${currency || ''}`.trim()
}

function studioLabel(candidate: StudioStateChangeCandidate): string {
  const number = candidate.studio_number ?? candidate.studio_internal_id
  return `№${number} · ${candidate.city_name ?? 'город не указан'}`
}

function cancellationStudioLabel(candidate: StudioCancellationCandidate): string {
  const number = candidate.studio_number ?? candidate.studio_internal_id
  return `№${number} · ${candidate.city_name ?? 'город не указан'}`
}

function cancellationPartnerLabel(candidate: StudioCancellationPartnerCandidate): string {
  const fio = candidate.persons_fio.trim() || 'без персон'
  return `№${candidate.partner_number} · ${fio}`
}

export function StudioStateChangeModal({
  initialMode,
  onBack,
  onClose,
  onDone,
}: {
  initialMode: StudioStateChangeMode
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const mode = initialMode
  const [candidates, setCandidates] = useState<StudioStateChangeCandidate[]>([])
  const [cancellationCandidates, setCancellationCandidates] = useState<StudioCancellationCandidate[]>([])
  const [cancellationPartnerCandidates, setCancellationPartnerCandidates] = useState<StudioCancellationPartnerCandidate[]>([])
  const [loading, setLoading] = useState(true)
  const [studioQuery, setStudioQuery] = useState('')
  const [studioInternalId, setStudioInternalId] = useState<number | null>(null)
  const [cancelMode, setCancelMode] = useState<'studio' | 'partner'>('studio')
  const [cancelPartnerQuery, setCancelPartnerQuery] = useState('')
  const [cancelPartnerNumber, setCancelPartnerNumber] = useState<number | null>(null)
  const [createdAtFromDate, setCreatedAtFromDate] = useState(todayIso())
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('RUB')
  const [isBargainAllowed, setIsBargainAllowed] = useState(false)
  const [closureType, setClosureType] = useState<StudioClosureType>('STANDARD')
  const [saleDate, setSaleDate] = useState(todayIso())
  const [changedDate, setChangedDate] = useState(todayIso())
  const [newAddress, setNewAddress] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function loadReferences() {
    setLoading(true)
    try {
      if (mode === 'cancel_studio') {
        const [cancelRows, partnerRows] = await Promise.all([
          fetchStudioCancellationCandidates(),
          fetchStudioCancellationPartnerCandidates(),
        ])
        setCancellationCandidates(cancelRows)
        setCancellationPartnerCandidates(partnerRows)
        setCandidates([])
        setError(null)
        return
      }

      const rows = mode === 'change_address'
        ? await fetchStudioAddressChangeCandidates()
        : await fetchStudioStateChangeCandidates()
      setCandidates(rows)
      setError(null)
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить список студий'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadReferences()
  }, [])

  const filteredCandidates = useMemo(() => {
    return candidates
      .filter(row => (mode === 'update_sale_offer' || mode === 'cancel_sale_offer') ? row.active_offer_count > 0 : true)
      .filter(row => mode === 'resume_studio' ? row.status_code === 'SUSPENDED' || row.status_code === 'SERVICE' : true)
      .filter(row => mode === 'transfer_to_management' ? row.studio_number !== null && (row.owner_phase === 'OWNED' || row.owner_phase === 'FOR_SALE') : true)
      .filter(row => matchesQuery([
        row.studio_number,
        row.city_name,
        row.country,
        row.address,
        row.owner_partner_number,
        row.owner_persons_fio,
        row.status_code,
        row.owner_phase,
      ], studioQuery))
      .slice(0, 80)
  }, [candidates, mode, studioQuery])

  const filteredCancellationCandidates = useMemo(() => {
    return cancellationCandidates
      .filter(row => matchesQuery([
        row.studio_number,
        row.studio_internal_id,
        row.city_name,
        row.country,
        row.address,
        row.owner_partner_number,
        row.owner_persons_fio,
        row.status_code,
        row.owner_phase,
      ], studioQuery))
      .slice(0, 80)
  }, [cancellationCandidates, studioQuery])

  const filteredCancellationPartnerCandidates = useMemo(() => {
    return cancellationPartnerCandidates
      .filter(row => matchesQuery([
        row.partner_number,
        row.persons_fio,
        row.cities_text,
        row.planned_studio_count,
      ], cancelPartnerQuery))
      .slice(0, 80)
  }, [cancelPartnerQuery, cancellationPartnerCandidates])

  const selectedStudio = useMemo(
    () => candidates.find(row => row.studio_internal_id === studioInternalId) ?? null,
    [candidates, studioInternalId]
  )

  const selectedCancellationStudio = useMemo(
    () => cancellationCandidates.find(row => row.studio_internal_id === studioInternalId) ?? null,
    [cancellationCandidates, studioInternalId]
  )

  const selectedCancellationPartner = useMemo(
    () => cancellationPartnerCandidates.find(row => row.partner_number === cancelPartnerNumber) ?? null,
    [cancelPartnerNumber, cancellationPartnerCandidates]
  )

  useEffect(() => {
    if (loading) return
    if (mode === 'cancel_studio') {
      if (cancelMode !== 'studio') return
      if (studioInternalId !== null && filteredCancellationCandidates.some(row => row.studio_internal_id === studioInternalId)) return
      setStudioInternalId(filteredCancellationCandidates.length === 1 ? filteredCancellationCandidates[0].studio_internal_id : null)
      return
    }
    if (studioInternalId !== null && filteredCandidates.some(row => row.studio_internal_id === studioInternalId)) return
    setStudioInternalId(filteredCandidates.length === 1 ? filteredCandidates[0].studio_internal_id : null)
  }, [cancelMode, filteredCandidates, filteredCancellationCandidates, loading, mode, studioInternalId])

  useEffect(() => {
    if (loading || mode !== 'cancel_studio' || cancelMode !== 'partner') return
    if (cancelPartnerNumber !== null && filteredCancellationPartnerCandidates.some(row => row.partner_number === cancelPartnerNumber)) return
    setCancelPartnerNumber(filteredCancellationPartnerCandidates.length === 1 ? filteredCancellationPartnerCandidates[0].partner_number : null)
  }, [cancelMode, cancelPartnerNumber, filteredCancellationPartnerCandidates, loading, mode])

  const normalizedPrice = Number(price.replace(/\s/g, '').replace(',', '.'))
  const canSubmit = !saving
    && !loading
    && (mode === 'cancel_studio'
      ? Boolean(changedDate) && (cancelMode === 'studio'
        ? Boolean(studioInternalId !== null && selectedCancellationStudio)
        : Boolean(cancelPartnerNumber && createdAtFromDate))
      : studioInternalId !== null
        && (mode === 'sale_offer' || mode === 'update_sale_offer'
          ? (Number.isFinite(normalizedPrice) && normalizedPrice > 0 && Boolean(saleDate))
          : mode === 'change_address'
            ? Boolean(changedDate && newAddress.trim() && selectedStudio?.studio_number)
            : Boolean(changedDate)))

  async function handleSubmit() {
    if (!canSubmit) return
    if (mode !== 'cancel_studio' && studioInternalId === null) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      if (mode === 'sale_offer') {
        const result = await createStudioSaleOffer({
          studioInternalId,
          price: normalizedPrice,
          isBargainAllowed,
          currency: currency || null,
          changedAt: dateAtTen(saleDate),
          note,
        })
        setSuccess(
          `Студия №${result.studio_number ?? result.studio_internal_id} выставлена на продажу: `
          + `${formatMoney(result.price, result.currency)}, торг ${result.is_bargain_allowed ? 'разрешен' : 'не разрешен'}.`
        )
      } else if (mode === 'update_sale_offer') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для смены оффера нужен номер студии')
        }

        const result = await updateStudioSaleOfferFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          price: normalizedPrice,
          isBargainAllowed,
          currency: currency || null,
          changedAt: dateAtTen(saleDate) ?? '',
        })
        setSuccess(
          `Оффер студии №${result.studio_number} изменен: `
          + `${formatMoney(result.price, result.currency)}, торг ${result.is_bargain_allowed ? 'разрешен' : 'не разрешен'}.`
        )
      } else if (mode === 'cancel_sale_offer') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для закрытия оффера нужен номер студии')
        }

        const result = await cancelStudioSaleOfferFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          changedAt: dateAtTen(changedDate) ?? '',
        })
        setSuccess(
          `Оффер студии №${result.studio_number} закрыт. Закрыто офферов: ${result.closed_offers_count}.`
          + (result.phase_changed ? ' Владение возвращено в статус «владеет».' : '')
        )
      } else if (mode === 'service_status') {
        const result = await setStudioServiceStatus({
          studioInternalId,
          changedAt: dateAtTen(changedDate),
          note,
        })
        setSuccess(
          `Студия №${result.studio_number ?? result.studio_internal_id} переведена в статус «нет продаж».`
        )
      } else if (mode === 'resume_studio') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для возобновления нужен номер студии')
        }

        const result = await resumeStudioFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          changedAt: dateAtTen(changedDate) ?? '',
        })
        setSuccess(`Студия №${result.studio_number} возвращена в статус «открыта».`)
      } else if (mode === 'suspend_studio') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для приостановки нужен номер студии')
        }

        const result = await suspendStudioFromOriginalCase({
          studioNumber: selectedStudio.studio_number,
          changedAt: dateAtNoon(changedDate) ?? '',
        })
        setSuccess(`Студия №${result.studio_number} переведена в статус «приостановлена».`)
      } else if (mode === 'close_studio') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для закрытия нужен номер студии')
        }

        const result = await closeStudioFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          changedAt: dateAtTwo(changedDate) ?? '',
          closureType,
        })
        setSuccess(
          `Студия №${result.studio_number} закрыта. Офферов закрыто: ${result.closed_offers_count}.`
        )
      } else if (mode === 'cancel_studio') {
        if (cancelMode === 'studio') {
          if (studioInternalId === null) {
            throw new Error('Выберите студию')
          }

          const result = await cancelStudioByIdFromOriginalCase({
            studioInternalId,
            changedAt: dateAtNoon(changedDate) ?? '',
          })
          setSuccess(
            `Студия №${result.studio_number ?? result.studio_internal_id} отменена. `
            + 'Владение переведено в статус «не открылась».'
          )
        } else {
          if (cancelPartnerNumber === null) {
            throw new Error('Выберите франчайзи')
          }

          const result = await cancelStudioByPartnerFromOriginalCase({
            partnerNumber: cancelPartnerNumber,
            createdAtFrom: dateAtStart(createdAtFromDate) ?? '',
            changedAt: dateAtNoon(changedDate) ?? '',
          })
          setSuccess(
            `Студия №${result.studio_number ?? result.studio_internal_id} франчайзи №${result.partner_number} отменена. `
            + 'Владение переведено в статус «не открылась».'
          )
        }
      } else if (mode === 'transfer_to_management') {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для передачи в УК нужен номер студии')
        }

        const result = await transferStudioToManagementFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          changedAt: dateAtTen(changedDate) ?? '',
          managementPartnerNumber: 1000,
        })
        setSuccess(
          `Студия №${result.studio_number} передана в УК на франчайзи №${result.new_partner_number}. `
          + `Старых офферов закрыто: ${result.closed_offers_count}. Новый оффер создан.`
        )
      } else {
        if (!selectedStudio?.studio_number) {
          throw new Error('Для смены адреса нужен номер студии')
        }

        const result = await changeStudioAddressFromOriginalCase({
          studioNumber: String(selectedStudio.studio_number),
          newAddress,
          changedAt: dateAtTen(changedDate) ?? '',
        })
        setSuccess(
          result.updated_count > 0
            ? `Адрес студии №${result.studio_number} изменен на «${result.new_address}».`
            : `Адрес студии №${result.studio_number} уже указан как «${result.new_address}».`
        )
        setNewAddress('')
      }

      setNote('')
      await loadReferences()
      onDone?.()
    } catch (submitError: unknown) {
      setError(errorMessage(submitError, 'Операция не выполнена'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8" onClick={onClose}>
      <section
        className="my-6 w-full max-w-3xl rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Вернуться к выбору кейса"
                aria-label="Вернуться к выбору кейса"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">{MODE_LABELS[mode]}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Выберите студию и заполните параметры операции.
            </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {success && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{success}</div>
          </div>
        )}

        {mode === 'cancel_studio' && (
          <div className="mb-3 flex flex-wrap gap-1 rounded-md border border-border bg-muted/30 p-1 text-sm">
            {([
              ['studio', 'По студии'],
              ['partner', 'По франчайзи'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setCancelMode(value)
                  setStudioInternalId(null)
                  setCancelPartnerNumber(null)
                  setError(null)
                  setSuccess(null)
                }}
                className={`rounded px-3 py-1.5 ${cancelMode === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0">
            {mode === 'cancel_studio' && cancelMode === 'partner' ? (
              <label className="block text-xs font-medium text-muted-foreground">
                Франчайзи
                <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
                  <Search size={15} className="text-muted-foreground" />
                  <input
                    value={cancelPartnerQuery}
                    onChange={event => setCancelPartnerQuery(event.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    placeholder="Номер, ФИО, город"
                  />
                </div>
                {loading ? (
                  <div className="mt-1 flex h-[154px] items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    Загрузка
                  </div>
                ) : (
                  <select
                    value={cancelPartnerNumber ?? ''}
                    onChange={event => {
                      const value = event.target.value
                      setCancelPartnerNumber(value ? Number(value) : null)
                      setSuccess(null)
                      setError(null)
                    }}
                    size={6}
                    className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
                  >
                    {filteredCancellationPartnerCandidates.map(candidate => (
                      <option key={candidate.partner_number} value={candidate.partner_number}>
                        {cancellationPartnerLabel(candidate)}
                        {candidate.cities_text ? ` · ${candidate.cities_text}` : ''}
                        {` · плановых студий: ${candidate.planned_studio_count}`}
                      </option>
                    ))}
                  </select>
                )}
                <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                  Показано {filteredCancellationPartnerCandidates.length} из {cancellationPartnerCandidates.length}
                </span>
              </label>
            ) : (
              <div className="block text-xs font-medium text-muted-foreground">
                <label>
                  Студия
                  <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
                    <Search size={15} className="text-muted-foreground" />
                    <input
                      value={studioQuery}
                      onChange={event => setStudioQuery(event.target.value)}
                      className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                      placeholder="Номер, город, адрес, франчайзи"
                    />
                  </div>
                </label>

                {loading ? (
                  <div className="mt-1 flex h-[154px] items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    Загрузка
                  </div>
                ) : (
                  <select
                    value={studioInternalId ?? ''}
                    onChange={event => {
                      const value = event.target.value
                      setStudioInternalId(value ? Number(value) : null)
                      setSuccess(null)
                      setError(null)
                    }}
                    size={6}
                    className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
                  >
                    {(mode === 'cancel_studio' ? filteredCancellationCandidates : filteredCandidates).map(candidate => (
                      <option key={candidate.studio_internal_id} value={candidate.studio_internal_id}>
                        {mode === 'cancel_studio' ? cancellationStudioLabel(candidate) : studioLabel(candidate)}
                        {candidate.owner_partner_number ? ` · №${candidate.owner_partner_number} ${candidate.owner_persons_fio}` : ' · без владельца'}
                        {candidate.owner_phase ? ` · ${PHASE_LABELS[candidate.owner_phase] ?? candidate.owner_phase}` : ''}
                        {'active_offer_count' in candidate && candidate.active_offer_count > 0 ? ` · оффер ${formatMoney(candidate.active_offer_price, candidate.active_offer_currency)}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                  Показано {mode === 'cancel_studio' ? filteredCancellationCandidates.length : filteredCandidates.length} из {mode === 'cancel_studio' ? cancellationCandidates.length : candidates.length}
                </span>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-3">
            {(mode === 'cancel_studio' ? selectedCancellationStudio : selectedStudio) && (
              <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                {(() => {
                  const displayStudio = mode === 'cancel_studio' ? selectedCancellationStudio : selectedStudio
                  if (!displayStudio) return null
                  return (
                    <>
                      <div className="text-sm font-semibold text-foreground">
                        {mode === 'cancel_studio' ? cancellationStudioLabel(displayStudio as StudioCancellationCandidate) : studioLabel(displayStudio as StudioStateChangeCandidate)}
                      </div>
                      <div className="mt-1">{displayStudio.address || 'Адрес не указан'}</div>
                      <div className="mt-1">
                        Сейчас: {STATUS_LABELS[displayStudio.status_code ?? ''] ?? displayStudio.status_code ?? '—'} ·{' '}
                        {PHASE_LABELS[displayStudio.owner_phase ?? ''] ?? displayStudio.owner_phase ?? '—'}
                      </div>
                    </>
                  )
                })()}
              </div>
            )}

            {mode === 'cancel_studio' && cancelMode === 'partner' && selectedCancellationPartner && (
              <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <div className="text-sm font-semibold text-foreground">{cancellationPartnerLabel(selectedCancellationPartner)}</div>
                <div className="mt-1">{selectedCancellationPartner.cities_text || 'Город не указан'}</div>
                <div className="mt-1">
                  Плановых студий: {selectedCancellationPartner.planned_studio_count}
                </div>
              </div>
            )}

            {mode === 'sale_offer' || mode === 'update_sale_offer' ? (
              <>
                <label className="block text-xs font-medium text-muted-foreground">
                  {mode === 'update_sale_offer' ? 'Новая цена' : 'Цена'}
                  <input
                    value={price}
                    onChange={event => setPrice(event.target.value)}
                    className={INPUT_CLASS}
                    inputMode="decimal"
                    placeholder="1250000"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Валюта
                    <select value={currency} onChange={event => setCurrency(event.target.value)} className={INPUT_CLASS}>
                      {CURRENCY_OPTIONS.map(item => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-muted-foreground">
                    Дата изменения
                    <input
                      type="date"
                      value={saleDate}
                      onChange={event => setSaleDate(event.target.value)}
                      className={INPUT_CLASS}
                    />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={isBargainAllowed}
                    onChange={event => setIsBargainAllowed(event.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  Торг разрешен
                </label>
              </>
            ) : (
              <>
                <label className="block text-xs font-medium text-muted-foreground">
                  Дата изменения
                  <input
                    type="date"
                    value={changedDate}
                    onChange={event => setChangedDate(event.target.value)}
                    className={INPUT_CLASS}
                  />
                </label>
                {mode === 'close_studio' && (
                  <label className="block text-xs font-medium text-muted-foreground">
                    Тип закрытия
                    <select
                      value={closureType}
                      onChange={event => setClosureType(event.target.value as StudioClosureType)}
                      className={INPUT_CLASS}
                    >
                      {CLOSURE_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                {mode === 'cancel_studio' && cancelMode === 'partner' && (
                  <label className="block text-xs font-medium text-muted-foreground">
                    Искать плановые владения, созданные с даты
                    <input
                      type="date"
                      value={createdAtFromDate}
                      onChange={event => setCreatedAtFromDate(event.target.value)}
                      className={INPUT_CLASS}
                    />
                  </label>
                )}
                {mode === 'change_address' && (
                  <label className="block text-xs font-medium text-muted-foreground">
                    Новый адрес
                    <textarea
                      value={newAddress}
                      onChange={event => setNewAddress(event.target.value)}
                      className={`${INPUT_CLASS} min-h-[80px] resize-y`}
                      placeholder={selectedStudio?.address || 'Адрес'}
                    />
                  </label>
                )}
              </>
            )}

            {mode !== 'change_address'
              && mode !== 'update_sale_offer'
              && mode !== 'cancel_sale_offer'
              && mode !== 'cancel_studio'
              && mode !== 'resume_studio'
              && mode !== 'transfer_to_management' && (
              <label className="block text-xs font-medium text-muted-foreground">
                Примечание
                <textarea
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  className={`${INPUT_CLASS} min-h-[80px] resize-y`}
                  placeholder="Необязательно"
                />
              </label>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Закрыть
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {saving && <Loader2 size={15} className="mr-2 animate-spin" />}
                Провести
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
